import { merge } from 'lodash';
import Default from '../../../common/Themes/Default/Theme';
import { sectionHeadingAlignment, sectionHeadingHR } from '../../../common/utils/themeOverrides';
import { getMenuHeadingStyle } from '../../../common/utils/legacyOverrides';
import * as utils from '../../../common/Themes/Default/Dials/Colors/utils';
import * as boldIcons from '../../../common/IconPacks/boldOutline';
import * as socialIconPack from '../../../common/IconPacks/SocialIcons/defaultSocialIconPack';
import { Cascade } from '../../../common/loaders';
import { sectionHrTypes, WIDGETS_WITH_CIRCULAR_IMAGES } from '../../../common/constants';
import { mergeTypographyOverrides } from '../../../common/utils/typography';
import themeConfig from '../themeConfig';

const { SMALL_UNDERLINE } = sectionHrTypes;

class Theme12 extends Default {
  static config = themeConfig;

  static get displayName() {
    return 'Theme12';
  }

  static getMutatorDefaultProps(widgetType, widgetPreset) {
    const defaultProps = super.getMutatorDefaultProps(widgetType, widgetPreset);
    const enableCircularImage =
      WIDGETS_WITH_CIRCULAR_IMAGES[widgetPreset] || defaultProps.enableCircularImage;

    return widgetType === 'HEADER'
      ? {
        ...defaultProps,
        hasLogoAlign: true,
        useSocialLinks: true,
        phoneOnSecondaryPage: true,
        headerTreatmentsConfig: {
          ...defaultProps.headerTreatmentsConfig,
          ...themeConfig.headerTreatmentsConfig
        }
      }
      : {
        ...defaultProps,
        enableCircularImage
      };
  }

  constructor() {
    super();
    this.mappedValues = {
      ...this.mappedValues,
      backgroundColorPrimary() {
        const [primary] = utils.getDial(this, ['primary']);
        return primary;
      },
      backgroundColorPrimaryLight() {
        const [primary] = utils.getDial(this, ['primary']);
        return primary.setAlpha(50);
      },
      typographyOverrides: {
        LogoAlpha: () => {
          const { category } = this.base;
          return {
            style: {
              font: 'primary',
              color: category === 'accent' ? 'highContrast' : 'highlight',
              fontSize: 'xlarge',
              fontWeight: 'normal',
              letterSpacing: 'normal',
              textTransform: 'none'
            }
          };
        },
        HeadingAlpha: {
          style: {
            font: 'primary',
            color: 'highContrast',
            fontSize: 'xxlarge',
            fontWeight: 'normal',
            letterSpacing: 'normal',
            textTransform: 'none'
          }
        },
        HeadingBeta: {
          style: {
            font: 'primary',
            color: 'highlight',
            fontSize: 'xxlarge',
            fontWeight: 'normal',
            letterSpacing: 'normal',
            textTransform: 'none'
          }
        },
        NavAlpha: {
          style: {
            font: 'alternate',
            color: 'highContrast',
            fontSize: 'small',
            fontWeight: 'normal',
            letterSpacing: '0.143em',
            textTransform: 'uppercase'
          }
        },
        SubNavAlpha: {
          style: {
            font: 'alternate',
            color: 'highlight',
            fontSize: 'small',
            fontWeight: 'normal',
            letterSpacing: 'normal',
            textTransform: 'none'
          }
        },
        NavBeta: {
          style: {
            font: 'alternate',
            color: 'highContrast',
            fontSize: 'large',
            fontWeight: 'normal',
            letterSpacing: 'normal',
            textTransform: 'none'
          }
        },
        SubNavBeta: {
          style: {
            font: 'alternate',
            color: 'section',
            fontSize: 'medium',
            fontWeight: 'normal',
            letterSpacing: 'normal',
            textTransform: 'none'
          }
        },
        InputAlpha: props => {
          return merge(mergeTypographyOverrides.call(this, 'BodyAlpha', props), {
            style: {
              color: 'input',
              ['@xs-only']: {
                fontSize: 'medium' // 16px
              }
            }
          });
        },
        ButtonAlpha: props => {
          const { size = 'default' } = props;
          const sizes = {
            small: {
              fontSize: 'small'
            },
            default: {
              fontSize: 'medium'
            },
            large: {
              fontSize: 'medium'
            }
          };
          return {
            style: {
              font: 'alternate',
              fontWeight: 'bold',
              letterSpacing: '0.143em',
              textTransform: 'uppercase',
              ...sizes[size]
            }
          };
        }
      }
    };
  }

  Section(props) {
    const footerStyles =
      this.base.widgetType === 'FOOTER'
        ? {
          borderColor: 'section',
          borderTopWidth: 'xsmall',
          borderTopStyle: 'solid'
        }
        : {};

    return super.Section(
      this.merge(
        {
          style: footerStyles
        },
        props
      )
    );
  }

  Heading(props) {
    const { tag } = props;
    const { widgetType, widgetPreset } = this.base;

    return super.Heading(
      this.merge({ style: getMenuHeadingStyle(tag, widgetType, widgetPreset) }, props)
    );
  }

  Tagline(props) {
    return super.Tagline(
      this.merge(
        {
          style: {
            maxWidth: '100%'
          }
        },
        props
      )
    );
  }

  HeroText(props) {
    return super.SubTagline(
      this.merge(
        {
          style: {
            maxWidth: '100%'
          }
        },
        props
      )
    );
  }

  Icon(props) {
    return super.Icon(
      this.merge(
        {
          iconPack: { ...boldIcons, ...socialIconPack }
        },
        props
      )
    );
  }

  Loader(props) {
    return Cascade.apply(this, [props]);
  }

  SectionHeading(props) {
    const base = this.base;
    const overrides = this.merge(
      {
        style: {
          textAlign: 'center',
          marginLeft: 'auto',
          ['@md']: {
            textAlign: 'center',
            marginLeft: 'auto'
          }
        }
      },
      sectionHeadingAlignment(base),
      sectionHeadingHR(base)
    );
    return super.SectionHeading(
      this.merge({ sectionHeadingHR: SMALL_UNDERLINE }, overrides, props)
    );
  }

  Input(props) {
    return super.Input(
      this.merge(
        {
          style: {
            borderColor: 'input',
            borderWidth: 'xsmall',
            borderRadius: 'medium',
            paddingVertical: 'small',
            paddingHorizontal: 'small',
            borderStyle: 'solid'
          }
        },
        props
      )
    );
  }

  InputFloatLabelLabel(props) {
    return super.InputFloatLabelLabel(
      this.merge(
        {
          style: {
            left: '16px',
            top: '33%'
          }
        },
        props
      )
    );
  }

  InputTextArea(props) {
    return super.InputTextArea(
      this.merge(
        {
          rows: 6
        },
        props
      )
    );
  }

  InputSelect(props) {
    return super.InputSelect(
      this.merge(
        {
          style: {
            borderWidth: 'xsmall',
            borderRadius: 'medium',
            borderStyle: 'solid'
          }
        },
        props
      )
    );
  }

  InputSelectElement(props) {
    return super.InputSelectElement(
      this.merge(
        {
          style: {
            paddingVertical: 'small',
            paddingHorizontal: 'small'
          }
        },
        props
      )
    );
  }

  MediaObjectBackground(props) {
    return super.MediaObjectBackground(
      this.merge(
        {
          style: {
            borderRadius: 'medium'
          }
        },
        props
      )
    );
  }

  HeadingMajor(props) {
    return super.HeadingMajor(this.merge({ typography: 'HeadingAlpha' }, props));
  }

  FooterLink(props) {
    return super.FooterLink(this.merge({ typography: 'NavAlpha' }, props));
  }

  NavMenuLink(props) {
    return super.NavMenuLink(this.merge({ typography: 'NavAlpha' }, props));
  }

  Intro(props) {
    return super.Intro(this.merge({ alignment: 'center' }, props));
  }

  ContentCard(props) {
    const styles = this.base.widgetPreset === 'about1' ? { style: { alignItems: 'center' } } : {};
    return super.ContentCard(this.merge(styles, props));
  }
}

export default Theme12;
